
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Apprenant {
  id_apprenant: string;
  nom: string;
  prenom: string;
}

export default function SatisfactionPage() {
  const navigate = useNavigate();
  const [apprenants, setApprenants] = useState<Apprenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    apprenant_id: '',
    jour: '',
    contenu_clair: '',
    attentes_ok: '',
    formateur_clair: '',
    interactions_ok: '',
    organisation_ok: '',
    utile: '',
    satisfaction_globale: '',
    commentaires_positifs: '',
    commentaires_amelioration: ''
  });

  useEffect(() => {
    // Vérifier l'autorisation d'accès
    const isAuthorized = localStorage.getItem('satisfaction_autorise') === 'true';
    if (!isAuthorized) {
      navigate('/login-satisfaction');
      return;
    }

    loadApprenants();
  }, [navigate]);

  const loadApprenants = async () => {
    try {
      const { data, error } = await supabase
        .from('apprenants')
        .select('id_apprenant, nom, prenom')
        .order('nom', { ascending: true });

      if (error) throw error;
      setApprenants(data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des apprenants:', err);
      setError('Erreur lors du chargement des apprenants');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase
        .from('satisfactionjournee')
        .insert([{
          apprenant_id: formData.apprenant_id,
          date_journee: new Date().toISOString().split('T')[0],
          jour: formData.jour,
          contenu_clair: formData.contenu_clair,
          attentes_ok: formData.attentes_ok,
          formateur_clair: formData.formateur_clair,
          interactions_ok: formData.interactions_ok,
          organisation_ok: formData.organisation_ok,
          utile: formData.utile,
          satisfaction_globale: formData.satisfaction_globale,
          commentaires_positifs: formData.commentaires_positifs,
          commentaires_amelioration: formData.commentaires_amelioration
        }]);

      if (error) throw error;

      // Supprimer l'autorisation après soumission
      localStorage.removeItem('satisfaction_autorise');
      
      navigate('/merci-satisfaction');
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement:', err);
      setError('Erreur lors de l\'enregistrement. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const jours = ['Jour 1', 'Jour 2', 'Jour 3', 'Jour 4', 'Jour 5'];
  const evaluationOptions = ['Très bien', 'Bien', 'Moyen', 'Insuffisant'];
  const satisfactionOptions = ['★★★★★', '★★★★☆', '★★★☆☆', '★★☆☆☆', '★☆☆☆☆'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4">
              <img 
                src="https://static.readdy.ai/image/4e4134bdead6558166f22511b6228c41/7838ad8dc199dc1e998c90a1f4b29262.png" 
                alt="DKBS Formation & Consulting" 
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Questionnaire de Satisfaction
            </h1>
            <p className="text-gray-600">
              Évaluez votre journée de formation
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start space-x-3">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-600 text-sm"></i>
              </div>
              <p className="text-sm text-red-700 flex-1">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations obligatoires */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Informations</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Votre nom *
                  </label>
                  <select
                    value={formData.apprenant_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, apprenant_id: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Sélectionnez votre nom</option>
                    {apprenants.map(apprenant => (
                      <option key={apprenant.id_apprenant} value={apprenant.id_apprenant}>
                        {apprenant.prenom} {apprenant.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Jour de formation *
                  </label>
                  <select
                    value={formData.jour}
                    onChange={(e) => setFormData(prev => ({ ...prev, jour: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Sélectionnez le jour</option>
                    {jours.map(jour => (
                      <option key={jour} value={jour}>{jour}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Questions d'évaluation */}
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Évaluation de la journée</h2>
              
              <div className="space-y-6">
                {[
                  { key: 'contenu_clair', label: 'Le contenu était clair et compréhensible' },
                  { key: 'attentes_ok', label: 'Les explications répondent à mes attentes' },
                  { key: 'formateur_clair', label: 'Le formateur a été clair et disponible' },
                  { key: 'interactions_ok', label: 'Les échanges étaient suffisants' },
                  { key: 'organisation_ok', label: 'L\'organisation (horaires, rythme, outils) était satisfaisante' },
                  { key: 'utile', label: 'Ce que j\'ai appris me sera utile pour mon projet' }
                ].map((question) => (
                  <div key={question.key}>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {question.label}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {evaluationOptions.map(option => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, [question.key]: option }))}
                          className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                            formData[question.key as keyof typeof formData] === option
                              ? 'bg-blue-600 text-white shadow-lg'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-blue-50'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Satisfaction globale */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Satisfaction globale de cette journée
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {satisfactionOptions.map(option => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, satisfaction_globale: option }))}
                        className={`px-6 py-3 rounded-xl text-lg font-medium transition-all ${
                          formData.satisfaction_globale === option
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'bg-white border border-gray-200 text-gray-700 hover:bg-blue-50'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Commentaires */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Vos commentaires</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ce que vous avez aimé aujourd'hui
                  </label>
                  <textarea
                    value={formData.commentaires_positifs}
                    onChange={(e) => setFormData(prev => ({ ...prev, commentaires_positifs: e.target.value }))}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    rows={3}
                    placeholder="Partagez ce qui vous a plu..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.commentaires_positifs.length}/500 caractères
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ce que vous aimeriez améliorer demain
                  </label>
                  <textarea
                    value={formData.commentaires_amelioration}
                    onChange={(e) => setFormData(prev => ({ ...prev, commentaires_amelioration: e.target.value }))}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    rows={3}
                    placeholder="Vos suggestions d'amélioration..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.commentaires_amelioration.length}/500 caractères
                  </p>
                </div>
              </div>
            </div>

            {/* Bouton de soumission */}
            <button
              type="submit"
              disabled={loading || !formData.apprenant_id || !formData.jour}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line text-xl"></i>
                  <span>Envoyer mon évaluation</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-blue-600 transition-colors flex items-center justify-center space-x-1 mx-auto"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Retour à l'accueil</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
